/* eslint-disable */
"use client";

import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import dayjs from "dayjs";
import errorimg from "@/images/error.webp";
import { TrialFormStart, ProductSummary, LoadingSpinner } from "trimble-terra";
import { LayoutGeneral, LayoutGeneralProps } from "@/components/terra/layouts/LayoutGeneral/LayoutGeneral";
import { LayoutFAQs, LayoutFAQsProps } from "@/components/terra/layouts/LayoutFAQs/LayoutFAQs";
import EntitlementsForm from "@/components/client/EntitlementsForm.js/EntitlementsForm";
import { startTrial, getTrialFormId, getTrialDetails } from "@/lib/trials";
import { getAccounts } from "@/lib/FlyoutCart/api";
import getConfigTID from "../../../lib/getConfigTID";
import { useLogin } from "@/context/login/LoginContext";
import { refreshAuthToken } from "@/lib/refreshAuthToken";
import { handleButtons } from "@/lib/utils";
import DatalayerTrial, { triggerTrialStarted } from "@/components/client/Datalayer/DatalayerTrial";
import { renderMarkdownToHtml } from "@/lib/formatters/renderMarkdown";
interface TrialWorkFlowProps {
  data: any;
  appData: any;
  locale: string;
  siteIdentifier: string;
  dataStorageEnv: string;
  dataStorageSpaceId: string;
  dataStorageAccessToken: string;
  product: {
    sku: string;
    productName: string;
    shortDescription?: string;
    purchaseTerms?: string;
    billingTerms?: string;
    tooltipText?: string;
    annualVsMonthly?: string;
    longDescription?: string;
    launchLinkWeb?: string;
    trialDuration?: number;
  };
  cognitoForm: any;
}
interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  tid: string;
}
const TrialWorkFlow: React.FC<TrialWorkFlowProps> = ({
  data,
  appData,
  locale,
  siteIdentifier: _siteIdentifier,
  product,
  cognitoForm: _cognitoForm
}) => {
  // Prefer localized Title when set; fall back to Product Name.
  const displayProductName = data?.title || data?.productName;
  const [isHydrated, setIsHydrated] = useState(false);
  const [trialIdFromAPI, setTrialIdFromAPI] = useState<string | undefined>();
  // Manage auth and userInfo state
  const [auth, setAuth] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const storedUserInfo = sessionStorage.getItem("user_info");
      return storedUserInfo ? JSON.parse(storedUserInfo) : null;
    } catch (_error) {
      return null;
    }
  });
  // Check if user is logged
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsHydrated(true);
      const hasUserInfo = sessionStorage.getItem("user_info") !== null;
      setUserLoggedIn(hasUserInfo);

      // If user has info but auth is still false, trigger auth check
      if (hasUserInfo && !auth) {
        // Page refresh detected - re-initializing auth state
      }

      // Persist Impartner partner ID from UTM params into sessionStorage
      // so it survives OAuth redirects that strip query strings.
      const partnerId = new URLSearchParams(window.location.search).get("utm_impartnerId");
      if (partnerId) {
        sessionStorage.setItem("utm_impartnerId", partnerId);
      }
    }
  }, [auth]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (data?.partnerTrial && !sessionStorage.getItem("utm_impartnerId")) {
        setError(true);
      }
    }
  }, []);
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval>;
    const checkAuthState = () => {
      if (typeof window !== "undefined") {
        const hasUserInfo_0 = sessionStorage.getItem("user_info") !== null;
        if (hasUserInfo_0 !== userLoggedIn) {
          setUserLoggedIn(hasUserInfo_0);
          if (hasUserInfo_0) {
            try {
              const storedUserInfo_0 = sessionStorage.getItem("user_info");
              const parsedUserInfo = storedUserInfo_0 ? JSON.parse(storedUserInfo_0) : null;
              setUserInfo(parsedUserInfo);
            } catch (_error_0) {
              setUserInfo(null);
            }
          } else {
            setUserInfo(null);
            setAuth(false);
          }
        }
      }
    };
    if (typeof window !== "undefined") {
      pollInterval = setInterval(checkAuthState, 1000);
      return () => clearInterval(pollInterval);
    }
  }, [userLoggedIn]);
  useEffect(() => {
    const handleLoginComplete = () => {
      const hasUserInfo_1 = sessionStorage.getItem("user_info") !== null;
      setUserLoggedIn(hasUserInfo_1);
      if (hasUserInfo_1) {
        try {
          const storedUserInfo_1 = sessionStorage.getItem("user_info");
          const parsedUserInfo_0 = storedUserInfo_1 ? JSON.parse(storedUserInfo_1) : null;
          setUserInfo(parsedUserInfo_0);
        } catch (_error_1) {
          setUserInfo(null);
        }
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("userLoginComplete", handleLoginComplete);
      window.addEventListener("authStateChanged", handleLoginComplete);
      return () => {
        window.removeEventListener("userLoginComplete", handleLoginComplete);
        window.removeEventListener("authStateChanged", handleLoginComplete);
      };
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [activeTrial, setActiveTrial] = useState<any>(null);
  const [endedTrial, setEndedTrial] = useState(false);
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [daysLeftOfTrial, setDaysLeftOfTrial] = useState<number | null>(null);

  // Set up cognito form
  const defaultCognito = {
    formId: "133",
    id: "991760a4-ddaf-572c-afd8-d35d6761b36e"
  };
  const finalCognitoForm = data?.marketoFormForTrialSubmission?.formId ? data.marketoFormForTrialSubmission : data?.cognitoForm?.formId ? data.cognitoForm : defaultCognito;
  const [, setLocalError] = useState(false);
  const [localErrorText, setLocalErrorText] = useState("");

  // Product validation
  useEffect(() => {
    if (!product?.sku) {
      setLocalError(true);
      setLocalErrorText("The product you are looking for does not exist.");
    }
  }, [product]);
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];
  const timeStamp = loginState?.authTime ? Number(loginState.authTime) : null;
  const onLogin = useCallback((token: string, authTimeVal: string, idToken: string, refreshToken: string) => {
    loginDispatch?.({
      type: "SET_LOGIN",
      payload: {
        token,
        authTime: authTimeVal,
        idToken,
        refreshToken
      }
    });
  }, [loginDispatch]);
  const onLogout = useCallback(() => {
    loginDispatch?.({
      type: "LOG_OUT"
    });
  }, [loginDispatch]);
  const CHECK_INTERVAL = 1000 * 60 * 5; // Check every 5 minutes
  const INACTIVITY_TIMEOUT = 1000 * 60 * 50; // Refresh after 50 minutes of inactivity

  const activityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expirationCheckTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const finalUtmData = useMemo(() => {
    if (typeof window === "undefined") return {};
    const utmKeys = ["utm_campaign", "utm_content", "utm_source", "utm_medium", "utm_term"];
    const collectedUtms: Record<string, string> = {};
    const urlParams = new URLSearchParams(window.location.search);
    utmKeys.forEach(key => {
      const urlValue = urlParams.get(key);
      const storageValue = sessionStorage.getItem(key);
      if (urlValue) {
        collectedUtms[key] = urlValue;
      } else if (storageValue) {
        collectedUtms[key] = storageValue;
      }
    });
    return {
      UTMCampaign: collectedUtms.utm_campaign || "",
      UTMContent: collectedUtms.utm_content || "",
      UTMSource: collectedUtms.utm_source || "",
      UTMMedium: collectedUtms.utm_medium || "",
      UTMTerm: collectedUtms.utm_term || ""
    };
  }, []);
  const prefillData = useMemo(() => {
    if (!userInfo) {
      return {};
    }
    const embedUrl = typeof window !== "undefined" ? `https://${window.location.hostname}${window.location.pathname}` : "";
    return {
      FirstName: userInfo?.firstName || "",
      LastName: userInfo?.lastName || "",
      Email: userInfo?.email || "",
      TID: userInfo?.tid || "",
      SKU: product?.sku || "",
      EmbedURL: embedUrl,
      FormReferrerURL: embedUrl,
      ...finalUtmData
    };
  }, [userInfo, product?.sku, finalUtmData]);
  const {
    queryString,
    signUpQuery,
    redirect_uri,
    code_verifier,
    client_id,
    url
  } = getConfigTID();
  const resetActivityTimer = useCallback(() => {
    if (activityTimer.current) {
      clearTimeout(activityTimer.current);
    }
    if (client_id && url) {
      const currentPath: string = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}${window.location.pathname}` as string : "/";
      activityTimer.current = setTimeout(() => {
        refreshAuthToken(loginState?.refreshToken ?? null, client_id, redirect_uri, code_verifier, currentPath, undefined, undefined, () => setAuth(true), setLoading, onLogin, onLogout);
      }, INACTIVITY_TIMEOUT);
    }
  }, [client_id, url, redirect_uri, code_verifier, INACTIVITY_TIMEOUT, onLogin, onLogout, loginState?.refreshToken]);
  const handleUserActivity = useCallback(() => {
    resetActivityTimer();
  }, [resetActivityTimer]);
  useEffect(() => {
    const currentPath_0: string = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}${window.location.pathname}` as string : "/";
    const checkTokenExpiration = () => {
      if (timeStamp && client_id && url && !getTrialFormId(Number(timeStamp))) {
        refreshAuthToken(loginState?.refreshToken ?? null, client_id, redirect_uri, code_verifier, currentPath_0, undefined, undefined, () => setAuth(true), setLoading, onLogin, onLogout);
      }
    };
    if (timeStamp && client_id && url) {
      checkTokenExpiration();
      expirationCheckTimer.current = setInterval(checkTokenExpiration, CHECK_INTERVAL);
    }
    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keypress", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    return () => {
      clearInterval(expirationCheckTimer.current || undefined);
      clearTimeout(activityTimer.current || undefined);
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
    };
  }, [timeStamp, url, CHECK_INTERVAL, client_id, redirect_uri, code_verifier, handleUserActivity, onLogin, onLogout, loginState?.refreshToken]);
  const checkIfUserHasTrial = useCallback(() => {
    setLoading(true);
    if (userLoggedIn) {
      try {
        const authTime = loginState?.authTime;
        const timeStamp_0 = authTime ? Number(authTime) : null;
        const userId = loginState?.token;
        if (timeStamp_0 && getTrialFormId(timeStamp_0) && userId) {
          setAuth(true);
          getTrialDetails(product?.sku, setActiveTrial, setLoading, setError, setTrialIdFromAPI, loginState?.token ?? null, loginState?.authTime ?? null, async () => {
            // Only users without an existing trial reach onEligible.
            // Block the trial form when the TID is tied to multiple accounts.
            const accounts = await getAccounts(loginState?.token);
            if (Array.isArray(accounts) && accounts.length > 1) {
              setError(true);
              setErrorText(data?.multipleAccountsErrorText || "Your Trimble ID is associated with multiple accounts. Please contact our sales team to start a trial.");
            }
          });
        } else {
          setAuth(false);
          setLoading(false);
        }
      } catch (_e) {
        setAuth(false);
        setLoading(false);
        setError(true);
        setErrorText("Authentication error.");
      }
    } else {
      setAuth(false);
      setLoading(false);
    }
  }, [userLoggedIn, product?.sku, loginState?.token, loginState?.authTime, data?.multipleAccountsErrorText]);
  useEffect(() => {
    if (isHydrated && typeof window !== "undefined") {
      checkIfUserHasTrial();
    }
  }, [isHydrated, userLoggedIn, checkIfUserHasTrial]);
  useEffect(() => {
    const getLicensingData = () => {
      if (activeTrial) {
        if (activeTrial?.trialStatus === "ACTIVE_TRIAL") {
          const trialEndDate = dayjs(activeTrial?.previousTrial?.deactivationDate);
          const daysLeft = trialEndDate.diff(Date.now(), "day") + 1;
          setDaysLeftOfTrial(daysLeft);
        }
        if (activeTrial?.trialStatus === "EXPIRED_TRIAL") {
          setDaysLeftOfTrial(0);
          setEndedTrial(true);
        }
      }
    };
    if (typeof window !== "undefined") {
      getLicensingData();
    }
  }, [activeTrial]);
  const formatProductSummaryCard = () => {
    if (!daysLeftOfTrial || daysLeftOfTrial === 0 || daysLeftOfTrial < 0) {
      return {
        name: displayProductName,
        description: data?.trialSummaryCustomText,
        productLine: `0 ${data?.trialLengthPlural || "Days "}`
      };
    }
    return {
      name: displayProductName,
      description: data?.trialSummaryCustomText,
      productLine: daysLeftOfTrial && daysLeftOfTrial == 1 ? `${daysLeftOfTrial} ${data?.trialLengthSingular || "Day"}` : `${daysLeftOfTrial} ${data?.trialLengthPlural || "Days "}`,
      ctas: [{
        text: data?.downloadTextForButtonAfterTrialHasStarted || "Download",
        url: product?.launchLinkWeb,
        className: "primary--1"
      }, {
        text: data?.viewYourProductsLabel,
        url: process.env.NEXT_PUBLIC_PRODUCTS_LINK ? process.env.NEXT_PUBLIC_PRODUCTS_LINK : "https://accounts.trimble.com/products",
        className: "primary--2"
      }]
    };
  };
  const productSummary = {
    variant: !daysLeftOfTrial || daysLeftOfTrial === 0 || daysLeftOfTrial < 0 ? "product-trial-last-day" : "product-trial",
    summaryCard: formatProductSummaryCard(),
    labels: {
      productSummaryLabel: data?.trialSummaryLabel,
      productLineLabel: data?.daysRemainingLabel
    },
    columns: [{
      eyebrow: data?.inTrialProductSummary?.columnOneEyebrow,
      header: data?.inTrialProductSummary?.columnOneHeader,
      subheader: data?.inTrialProductSummary?.columnOneDescription,
      listType: data?.inTrialProductSummary?.columnOneBulletsCollection?.items?.[0]?.__typename === "Point" ? "bullets" : data?.inTrialProductSummary?.columnOneBulletsCollection?.items?.[0]?.__typename === "Link" ? "links" : null,
      list: formatList(data?.inTrialProductSummary?.columnOneBulletsCollection?.items || [], locale)
    }, {
      eyebrow: data?.inTrialProductSummary?.columnTwoEyebrow,
      header: data?.inTrialProductSummary?.columnTwoHeader,
      subheader: data?.inTrialProductSummary?.columnTwoDescription,
      listType: data?.inTrialProductSummary?.columnTwoBulletsCollection?.items?.[0]?.__typename === "Point" ? "bullets" : data?.inTrialProductSummary?.columnTwoBulletsCollection?.items?.[0]?.__typename === "Link" ? "links" : null,
      list: formatList(data?.inTrialProductSummary?.columnTwoBulletsCollection?.items || [], locale)
    }]
  };
  const trialSpecifics = {
    header: data?.trialFormStartLabel && displayProductName ? `${data?.trialFormStartLabel} ${displayProductName}` : `Start a Trial of ${displayProductName}`,
    text: product?.shortDescription || "",
    signInText: data?.signInTextLabel,
    ctas: [{
      text: data?.signInLabel || "",
      onClick: typeof window !== "undefined" ? () => {
        const state = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        window.open(`${queryString}${state}`, "_self");
      } :
      // window.open(
      //   `${queryString}/${locale}/products/${data?.productName
      //     .toLowerCase()
      //     .replaceAll(' ', '-')}/trial`,
      //   '_self'
      // )
      () => {},
      className: "primary--1"
    }, {
      text: data?.createAnAccountLabel || "",
      onClick: () => {
        const state_0 = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        window.open(`${signUpQuery}${state_0}`, "_self");
      },
      // window.open(
      //   `${signUpQuery}/${locale}/products/${data?.productName
      //     .toLowerCase()
      //     .replaceAll(' ', '-')}/trial`,
      //   '_self'
      // ),
      className: "primary--2"
    }]
  };
  const trialSummary = {
    eyebrow: data?.trialSummaryLabel || "",
    productName: displayProductName,
    productNameIcon: data?.productIcon?.file?.url ? {
      src: data?.productIcon?.file?.url,
      alt: data?.productIcon?.description || ""
    } : null,
    body: data?.trialSummaryCustomText,
    bullets: data?.customContentListForTrialSummary?.length ? data?.customContentListForTrialSummary : [],
    durationLabel: data?.durationLabel || "Duration",
    duration: product?.trialDuration && product?.trialDuration == 1 ? `${product?.trialDuration} ${data?.trialLengthSingular || "Day"}` : product?.trialDuration ? `${product?.trialDuration} ${data?.trialLengthPlural || "Days "}` : null,
    operatingSystemLabel: data?.operatingSystemTypeToDisplay ? data?.operatingSystemLabel || "" : "",
    operatingSystem: data?.operatingSystemTypeToDisplay
  };
  const content = {
    trialSpecifics,
    trialSummary
  };
  const formatTextWithCTA = (content_0: any) => {
    if (!content_0) return {};
    const buttons = formatCtaWithHeaderButtons(content_0, locale);
    const formatted = {
      presetTheme: "t2-theme-two",
      eyebrow: content_0?.eyebrow,
      header: content_0?.headline,
      description: renderMarkdownToHtml(content_0?.subhead),
      buttons
    };
    if (!formatted.eyebrow && !formatted.header && !formatted.description && !formatted.buttons?.length) {
      return {};
    }
    return formatted;
  };
  // Format accordion and CTA section
  const formatAccordionAndCTASection = (cta: any, accordion: any) => {
    let ctaSection: any = {};
    let accordionSection: any[] = [];
    if (cta) {
      ctaSection = {
        eyebrow: cta?.eyebrow,
        header: cta?.headline,
        subheader: cta?.subhead,
        description: renderMarkdownToHtml(cta?.body?.body || cta?.body),
        buttons: handleButtons(cta?.ctaButtonsCollection?.items, true, locale) || []
      };
    }
    if (accordion) {
      accordionSection = accordion?.itemsCollection?.items?.map((item: any) => {
        return {
          headline: item?.headline || item?.title,
          description: renderMarkdownToHtml(item?.body)
        };
      }) || [];
    }
    return {
      ...ctaSection,
      faqItems: accordionSection
    };
  };

  // Render questions and answers section
  const renderQuestionsAndAnswers = () => {
    const accordionData = data?.ctaWithTextAndHeaderWithAccordionForTrialsCollection?.items?.find((component: any) => {
      return component?.__typename === "Accordion";
    });
    const ctaWithHeader = data?.ctaWithTextAndHeaderWithAccordionForTrialsCollection?.items?.find((component_0: any) => {
      return component_0?.__typename === "CtaWithHeader";
    });
    return accordionData || ctaWithHeader ? <div className="grid px-[max(0px,calc((100%-1280px)/2))] m-0 grid-cols-[repeat(16,1fr)] bg-white w-screen -translate-x-1/2 ml-[50%] [&_.t2-layout-faq]:[grid-column:1/span_16]! [&_.t2-accordion--is-open]:bg-white! [&_.t2-layout-faq__cta]:!justify-start">
        <LayoutFAQs {...formatAccordionAndCTASection(ctaWithHeader, accordionData)} />
      </div> : null;
  };
  const isDataReady = useMemo(() => !!(!loading && auth && finalCognitoForm?.formId && product?.sku && userInfo), [loading, auth, finalCognitoForm?.formId, product?.sku, userInfo]);
  const displayErrorText = errorText || localErrorText;
  // Format error buttons from errorLinks data
  const errorButtons = data?.errorLinks?.length ? data?.errorLinks?.map((link: any) => ({
    primary: true,
    label: link?.text || link?.linkText || "Learn More",
    destination: link?.url || link?.urlString || "#"
  })) : [];
  const heroErrorContent = {
    orientation: "left" as const,
    isHeaderBig: false,
    isWrapped: true,
    presetTheme: "t2-theme-two",
    header: data?.anErrorOccurredLabel || "An Error has occurred",
    description: displayErrorText || data?.errorTextDescription || "There was an issue creating your trial. Please contact our sales team",
    buttons: errorButtons,
    image: data?.errorImage?.file?.url ? {
      alt: data?.errorImage?.description || "",
      x: 0,
      y: 0,
      title: data?.errorImage?.title || "error",
      height: 500,
      width: 500,
      url: errorimg.src
    } : {
      src: errorimg.src,
      alt: "error page"
    }
  };
  const styles = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontFamily: '"Open Sans", sans-serif'
  };
  if (!isHydrated) {
    return <section className="w-full">
        <div className="py-[91px] px-[max(0px,calc((100%-1280px)/2))] bg-gray-m1 grid grid-cols-[repeat(16,1fr)] [&_.t2-layout-trial-form]:[grid-column:2/span_14] [&_.t2-mxp-product-summary]:pt-[45px] [&_.t2-mxp-product-summary]:pl-[22px] [&_.t2-mxp-product-summary_.product-summary-column]:pl-20">
          <section className="bg-gray-m1 my-[250px] mx-auto [grid-column:8/span_1]">
            <LoadingSpinner styles={styles} />
          </section>
        </div>
      </section>;
  }
  const gridWrapperClasses = "py-[58px] px-[max(0px,calc((100%-1280px)/2))] bg-gray-m1 grid grid-cols-[repeat(16,1fr)] [&_.t2-layout-trial-form]:[grid-column:2/span_14] [&_.t2-mxp-product-summary]:pt-[45px] [&_.t2-mxp-product-summary]:pl-[22px] [&_.t2-mxp-product-summary_.product-summary-column]:pl-20 [&_.t2-product-summary-card--container__name-text]:!mb-1.5 [&_.product-summary-column__bullets-container_.product-summary-column__bullets]:list-disc [&_.product-summary-column__bullets-container_.product-summary-column__bullets]:text-lg [&_.product-summary-column__bullets-container_.product-summary-column__bullets]:pl-0.5 [&_.t2-mxp-product-summary_.t2-product-summary-card--container__name-description]:pb-5 [&_.t2-mxp-product-summary]:!pl-6";
  const contentContainerClasses = "mx-[10px] bg-gray-m1 grid grid-cols-[repeat(16,1fr)] ![grid-column:1/span_16] [&_.t2-trial-summary--product--name]:!m-0 [&_.t2-trial-specifics--top--text]:text-lg [&_.t2-trial-specifics--top--text]:not-italic [&_.t2-trial-specifics--top--text]:font-normal [&_.t2-trial-specifics--top--text]:leading-8 [&_.t2-trial-specifics--top--text]:tracking-[0.18px] [&_.t2-trial-specifics--bottom--text]:text-sm [&_.t2-trial-specifics--bottom--text]:not-italic [&_.t2-trial-specifics--bottom--text]:font-bold [&_.t2-trial-specifics--bottom--text]:leading-5 [&_.t2-trial-specifics--bottom--text]:tracking-[0.56px] [&_.t2-trial-specifics--bottom--text]:uppercase [&_.t2-trial-specifics--bottom--sign-in]:w-[251px] [&_.t2-trial-specifics--bottom--sign-in]:flex [&_.t2-trial-specifics--bottom--sign-in]:flex-col [&_.t2-trial-specifics--bottom--sign-in_.t2-button]:p-2 [&_.t2-trial-specifics--bottom--sign-in_.t2-button]:leading-9 [&_.t2-trial-specifics--bottom--sign-in_.t2-button]:text-base [&_.t2-trial-specifics--bottom--sign-in_.t2-button]:max-h-14 [&_.t2-trial-specifics--bottom--sign-in_.t2-button]:my-3";
  const startedEndedErrorClasses = "![grid-column:1/span_16] bg-gray-m1 grid [&_.t2-lo-general__image]:w-full [&_.t2-layout-faq]:pb-5 [&_.t2-layout-faq]:pl-[22px] [&_.t2-layout-faq__header]:mb-3 [&_.t2-layout-faq__primary-content]:p-0 [&_.t2-layout-faq__cta]:!justify-start";
  return <section className="w-full">
      <DatalayerTrial productName={product?.productName || ""} trialDuration={product?.trialDuration} />
      <div className={gridWrapperClasses}>
        {loading && <section className="bg-gray-m1 my-[250px] mx-auto [grid-column:8/span_1]">
            <LoadingSpinner />
          </section>}

        {!activeTrial && !loading && !error && !endedTrial && <div className={contentContainerClasses}>
            <section className="ml-[10px] [grid-column:1/span_16] [&_.t2-trial-specifics--top]:p-0 [&_.t2-trial-specifics--top]:[background-image:none]! [&_.t2-trial-specifics--bottom]:p-0 [&_.t2-trial-specifics--bottom]:[background-image:none]!">
              {/* Before Login */}
              {!auth && !loading && <TrialFormStart content={content} />}
              {/* After Logged before trial starts */}
              {isDataReady && <EntitlementsForm trialSummary={trialSummary} cognitoForm={finalCognitoForm} prefillData={prefillData} appData={appData} userInfo={userInfo} productData={product} startTrial={(setLoading_0, setError_0, setErrorText_0, sku, accessToken) => startTrial(setLoading_0, setError_0, setErrorText_0, sku, accessToken, () => triggerTrialStarted(product?.productName || "", product?.trialDuration, "ACTIVE_TRIAL"))} accessToken={loginState?.token ?? null} setLoading={setLoading} setError={setError} setErrorText={setErrorText} loading={loading} partnerPage={data?.partnerTrial} trialIdFromAPI={trialIdFromAPI} />}
            </section>
          </div>}
        {auth && activeTrial && !endedTrial && !loading && !error && <div className={startedEndedErrorClasses}>
            {/* Your trial has started */}
            {data?.inTrialMessagingHeaderTextAndCta && <LayoutFAQs {...formatTextWithCTA(data?.inTrialMessagingHeaderTextAndCta) as LayoutFAQsProps} />}
            {productSummary && <ProductSummary content={productSummary} />}
          </div>}
        {auth && endedTrial && !loading && !error && <div className={startedEndedErrorClasses}>
            {/* Your trial has ended */}
            {data?.yourTrialHasEndedHeaderTextAndCta && <LayoutFAQs {...formatTextWithCTA(data?.yourTrialHasEndedHeaderTextAndCta) as LayoutFAQsProps} />}
            {productSummary && <ProductSummary content={productSummary} />}
          </div>}
        {/* Error state */}
        {error && !loading && <div className={startedEndedErrorClasses}>
            {heroErrorContent && <LayoutGeneral {...heroErrorContent as LayoutGeneralProps} />}
          </div>}
      </div>
      {renderQuestionsAndAnswers()}
    </section>;
};
const formatCtaWithHeaderButtons = (content: any, locale?: string) => {
  const items = content?.ctaButtonsCollection?.items?.filter(Boolean) ?? content?.ctaButtons?.filter(Boolean) ?? [];
  if (!items.length) return null;
  const buttons = items.map((button: any) => handleButtons([button], true, locale)?.[0] ?? null).filter(Boolean);
  return buttons.length > 0 ? buttons : null;
};

// Format list function for bullet collections
const formatList = (list: any[], _locale: string) => {
  if (list && list.length) {
    if (list?.[0]?.__typename === "Point") {
      const result = list.map(item => ({
        title: item?.header,
        text: item?.description
      }));
      return result;
    }
    if (list?.[0]?.__typename === "Link") {
      const result = list.map(item => {
        let itemUrl = item?.url?.url;
        if (item?.url?.__typename === "ExternalLink") {
          itemUrl = item?.url?.url;
        }
        return {
          url: itemUrl,
          text: item?.text || item?.title
        };
      });
      return result;
    }
  }
  return [];
};
export default TrialWorkFlow;